import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Button,
  CircularProgress,
  Grid,
  Skeleton
} from '@mui/material';
import {
  SyncOutlined as SyncIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import ConfirmDialog from '../ConfirmDialog';

export default function SyncOperationsTab() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Fetch sync status first to initialize correctly
  const {
    data: syncStatusData,
    isLoading: isLoadingSyncStatus,
    refetch: refetchSyncStatus,
    error: syncStatusError
  } = useQuery({
    queryKey: ['productSyncOperationsSyncStatus'],
    queryFn: async () => {
      try {
        const response = await api.get('/product-sync/status');
        return response.data.data;
      } catch (error) {
        console.error('Error in sync status query:', error);
        throw error;
      }
    },
    refetchOnWindowFocus: true,
    refetchInterval: 5000, // Poll every 5 seconds
    enabled: true, // Always enabled
    staleTime: 0, // Always consider data stale to force a refresh
    retry: 3 // Retry failed requests 3 times
  });

  // Initialize based on current sync status - do this after the query is defined
  const [isSyncInProgress, setIsSyncInProgress] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: null // 'whole' or 'updated'
  });

  // Update the sync status whenever data changes
  useEffect(() => {
    if (syncStatusError) {
      console.error('Error fetching sync status:', syncStatusError);
      // Ensure buttons are enabled if there's an error
      setIsSyncInProgress(false);
      return;
    }

    // Check if sync is in progress - explicit check for isRunning property
    const syncRunning = syncStatusData?.isRunning === true;

    setIsSyncInProgress(syncRunning);
  }, [syncStatusData, syncStatusError, isSyncInProgress]);

  // Fetch sync settings
  const {
    data: syncSettings,
    isLoading: isLoadingSettings
  } = useQuery({
    queryKey: ['productSyncSettings'],
    queryFn: async () => {
      const response = await api.get('/product-sync/settings');
      return response.data.data;
    },
    staleTime: 0
  });

  // Fetch field mappings
  const {
    data: fieldMappings,
    isLoading: isLoadingFieldMappings
  } = useQuery({
    queryKey: ['productFieldMappings'],
    queryFn: async () => {
      const response = await api.get('/product-sync/field-mappings');
      return response.data.data;
    },
    staleTime: 0
  });

  // Check if all required settings are filled
  const areRequiredSettingsFilled = () => {
    if (!syncSettings) return false;

    const requiredFields = [
      'getItemsEndpoint',
      'getItemImageEndpoint',
      'image_category',
      'image_max_width',
      'image_max_height',
      'query_batch_size',
      'erp_query_columns',
      'whole_items_query',
      'updated_items_query',
      'product_notes_query',
      'product_tags_query',
      'product_variant_name'
    ];

    return requiredFields.every(field => {
      const value = syncSettings[field];
      return value !== null && value !== undefined && value !== '';
    });
  };

  // Check if field mappings exist
  const areFieldMappingsConfigured = () => {
    return fieldMappings && fieldMappings.length > 0;
  };

  // Start whole items sync mutation
  const startWholeItemsSyncMutation = useMutation({
    mutationFn: async () => {
      // Use the batch size from settings or fallback to 250
      const batchSize = syncSettings?.query_batch_size || 250;

      const response = await api.post('/product-sync/sync/whole-items', {
        query_batch_size: batchSize
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Whole items sync started successfully');
      queryClient.invalidateQueries({ queryKey: ['syncLogs'] });
      // Check sync status immediately after starting
      refetchSyncStatus();
      // Set sync in progress to true immediately for better UX
      setIsSyncInProgress(true);
    },
    onError: (error) => {
      toast.error(`Failed to start whole items sync: ${error.response?.data?.message || error.message}`);
    }
  });

  // Start updated items sync mutation
  const startUpdatedItemsSyncMutation = useMutation({
    mutationFn: async () => {
      // Use the batch size from settings or fallback to 250
      const batchSize = syncSettings?.query_batch_size || 250;

      const response = await api.post('/product-sync/sync/updated-items', {
        query_batch_size: batchSize
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success("Today's updated items sync started successfully");
      queryClient.invalidateQueries({ queryKey: ['syncLogs'] });
      // Check sync status immediately after starting
      refetchSyncStatus();
      // Set sync in progress to true immediately for better UX
      setIsSyncInProgress(true);
    },
    onError: (error) => {
      toast.error(`Failed to start updated items sync: ${error.response?.data?.message || error.message}`);
    }
  });

  return (
    <>
      <Grid container spacing={3}>
        {isLoadingSyncStatus || isLoadingSettings || isLoadingFieldMappings ? (
          // Detailed skeletons that match the card structure
          <>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                  <Box display='flex' alignItems='center' mb={2}>
                    <Skeleton variant='circular' width={24} height={24} sx={{ mr: 1 }} />
                    <Skeleton variant='text' width={150} />
                  </Box>
                  <Skeleton variant='text' width='90%' />
                  <Skeleton variant='text' width='100%' />

                  <Skeleton
                    variant='rectangular'
                    sx={{
                      mt: 2,
                      mb: 2,
                      height: 60,
                      borderRadius: 1,
                      bgcolor: 'rgba(41, 182, 246, 0.1)'
                    }}
                  />

                  <Skeleton
                    variant='rectangular'
                    height={36}
                    sx={{
                      borderRadius: 1,
                      bgcolor: 'primary.light',
                      opacity: 0.2
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                  <Box display='flex' alignItems='center' mb={2}>
                    <Skeleton variant='circular' width={24} height={24} sx={{ mr: 1 }} />
                    <Skeleton variant='text' width={150} />
                  </Box>
                  <Skeleton variant='text' width='90%' />
                  <Skeleton variant='text' width='100%' />

                  <Skeleton
                    variant='rectangular'
                    sx={{
                      mt: 2,
                      mb: 2,
                      height: 60,
                      borderRadius: 1,
                      bgcolor: 'rgba(41, 182, 246, 0.1)'
                    }}
                  />

                  <Skeleton
                    variant='rectangular'
                    height={36}
                    sx={{
                      borderRadius: 1,
                      bgcolor: 'primary.light',
                      opacity: 0.2
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Box mt={2} display='flex' justifyContent='center'>
                <Skeleton variant='rectangular' width={180} height={36} animation='wave' sx={{ borderRadius: 1, bgcolor: 'secondary.light', opacity: 0.3 }} />
              </Box>
            </Grid>
          </>
        ) : (
          // Actual content when not loading
          <>
            {!areRequiredSettingsFilled() && (
              <Grid item xs={12}>
                <Alert
                  severity='warning'
                  sx={{ mb: 2 }}
                  action={
                    <Button
                      color='inherit'
                      size='small'
                      onClick={() => navigate('/product-sync/settings')}
                    >
                    Configure Settings
                    </Button>
                  }
                >
                  <Typography variant='subtitle2' gutterBottom>
                  Configuration Required
                  </Typography>
                  <Typography variant='body2'>
                  Please configure all required settings before running sync operations.
                  </Typography>
                </Alert>
              </Grid>
            )}

            {!areFieldMappingsConfigured() && (
              <Grid item xs={12}>
                <Alert
                  severity='warning'
                  sx={{ mb: 2 }}
                  action={
                    <Button
                      color='inherit'
                      size='small'
                      onClick={() => navigate('/product-sync/mapping')}
                    >
                    Configure Mappings
                    </Button>
                  }
                >
                  <Typography variant='subtitle2' gutterBottom>
                  Field Mappings Required
                  </Typography>
                  <Typography variant='body2'>
                  Please configure field mappings to map ERP data to Shopify fields before running sync operations.
                  </Typography>
                </Alert>
              </Grid>
            )}
            {/* Sync in progress warning banner */}
            {isSyncInProgress && (
              <Grid item xs={12}>
                <Alert severity='warning' sx={{ mb: 0 }}>
                  Product synchronization is currently in progress. Sync operations are disabled until the sync completes.
                </Alert>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                  <Box display='flex' alignItems='center' mb={2}>
                    <SyncIcon color='primary' sx={{ mr: 1 }} />
                    <Typography variant='h6'>
                      Whole Items Sync
                    </Typography>
                  </Box>
                  <Typography variant='body2' color='text.secondary' paragraph>
                    Sync all eligible products from your ERP system to Shopify. This operation will create, update, or skip products based on their existence in Shopify.
                  </Typography>
                  <Alert severity='info' sx={{ mb: 2, fontSize: { xs: '12px'} }}>
                    This operation may take a long time depending on the number of products in your ERP system.
                    You can check the sync progress in the Sync Logs section.
                  </Alert>
                  <Button
                    variant='contained'
                    color='primary'
                    startIcon={startWholeItemsSyncMutation.isPending ? <CircularProgress size={20} color='inherit' /> : <SyncIcon />}
                    onClick={() => setConfirmDialog({ open: true, type: 'whole' })}
                    disabled={startWholeItemsSyncMutation.isPending || isSyncInProgress || !areRequiredSettingsFilled() || !areFieldMappingsConfigured()}
                    fullWidth
                    sx={{ width: { xs: '100% !important' } }}
                  >
                    {startWholeItemsSyncMutation.isPending ? 'Starting Sync...' :
                      isSyncInProgress ?
                        (syncStatusData?.activeSync?.syncSubType === 'whole_items' ?
                          'Whole Items Sync In Progress...' :
                          'Sync In Progress...') :
                        'Start Whole Items Sync'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                  <Box display='flex' alignItems='center' mb={2}>
                    <RefreshIcon color='primary' sx={{ mr: 1 }} />
                    <Typography variant='h6'>
                      Today's Updated Items Sync
                    </Typography>
                  </Box>
                  <Typography variant='body2' color='text.secondary' paragraph>
                    Sync only the items that have been updated in your ERP system today. This is a faster operation for regular updates.
                  </Typography>
                  <Alert severity='info' sx={{ mb: 2, fontSize: { xs: '12px'} }}>
                    This operation only syncs items that have been modified or created today in your ERP system.
                    You can check the sync progress in the Sync Logs section.
                  </Alert>
                  <Button
                    variant='contained'
                    color='primary'
                    startIcon={startUpdatedItemsSyncMutation.isPending ? <CircularProgress size={20} color='inherit' /> : <RefreshIcon />}
                    onClick={() => setConfirmDialog({ open: true, type: 'updated' })}
                    disabled={startUpdatedItemsSyncMutation.isPending || isSyncInProgress || !areRequiredSettingsFilled() || !areFieldMappingsConfigured()}
                    fullWidth
                    sx={{ width: { xs: '100% !important' } }}
                  >
                    {startUpdatedItemsSyncMutation.isPending ? 'Starting Sync...' :
                      isSyncInProgress ?
                        (syncStatusData?.activeSync?.syncSubType === 'updated_items' ?
                          'Updated Items Sync In Progress...' :
                          'Sync In Progress...') :
                        "Start Today's Updated Items Sync"}
                  </Button>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Box display='flex' justifyContent='center' sx={{ pb: { xs: '50px'} }}>
                <Button
                  variant='contained'
                  color='secondary'
                  startIcon={<HistoryIcon />}
                  onClick={() => navigate('/sync-logs?syncType=product&direction=erp_to_shopify')}
                >
                  View Sync Logs
                </Button>
              </Box>
            </Grid>
          </>
        )}
      </Grid>
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.type === 'whole' ? 'Start Whole Items Sync?' : "Start Today's Updated Items Sync?"}
        message={confirmDialog.type === 'whole'
          ? 'Are you sure you want to start a whole items sync? This may take a long time depending on the number of products.'
          : 'Are you sure you want to start today\'s updated items sync? This will only sync products updated or created today.'}
        confirmText={confirmDialog.type === 'whole' ? 'Start Whole Sync' : 'Start Updated Sync'}
        cancelText='Cancel'
        confirmColor='primary'
        isLoading={
          (confirmDialog.type === 'whole' && startWholeItemsSyncMutation.isPending) ||
          (confirmDialog.type === 'updated' && startUpdatedItemsSyncMutation.isPending)
        }
        onCancel={() => setConfirmDialog({ open: false, type: null })}
        onConfirm={() => {
          if (confirmDialog.type === 'whole') {
            startWholeItemsSyncMutation.mutate(undefined, {
              onSettled: () => setConfirmDialog({ open: false, type: null })
            });
          } else if (confirmDialog.type === 'updated') {
            startUpdatedItemsSyncMutation.mutate(undefined, {
              onSettled: () => setConfirmDialog({ open: false, type: null })
            });
          }
        }}
      />
    </>
  );
}
